import {
  getDiscussList,
  createDiscuss,
  deleteDiscuss,
  likeDiscuss,
  unlikeDiscuss,
  updateDiscuss
} from '@/services/project/comment'
import type { Effect, Reducer } from 'umi'
import type { CommentModelState } from './connect'

export interface CommentModelType {
  namespace: 'comment'
  state: CommentModelState
  effects: {
    getDiscussList: Effect
    refreshDiscussList: Effect
    createDiscuss: Effect
    updateDiscuss: Effect
    deleteDiscuss: Effect
    likeDiscuss: Effect
    unlikeDiscuss: Effect
  }
  reducers: {
    saveDiscussList: Reducer<CommentModelState>
    updateDiscussLike: Reducer<CommentModelState>
  }
}

const initialState = {
  log_comment: {},
  memo_comment: {},
  company_info_comment: {},
  minutes_meeting_comment: {},
  company_document_comment: {},
  touhou_manage_comment: {},
  pipeline_comment: {},
  spa_comment: {},
  pipeline_return_investment: {},
  portfolio_return_investment: {},
  sub_memo: {}
}

const CommentModel: CommentModelType = {
  namespace: 'comment',
  state: initialState,
  effects: {
    *getDiscussList({ payload }, { call, put }) {
      const { comment_type = 1, unique_id } = payload
      const response = yield call(getDiscussList, payload)
      const userIds: number[] = []
      response.data.discuss_infos.forEach(c => {
        userIds.push(c.creator.id)
        c.reply_discuss.forEach(_c => {
          userIds.push(_c.creator.id)
        })
      })

      yield put({
        type: 'global/fetchUserAvatar',
        payload: Array.from(new Set(userIds))
      })
      yield put({
        type: 'saveDiscussList',
        payload: {
          id: unique_id,
          comment_type,
          data: response.data.discuss_infos || {}
        }
      })
      return Promise.resolve()
    },
    *refreshDiscussList({ payload }, { call, put }) {
      const { comment_type = 1, unique_id } = payload
      const response = yield call(getDiscussList, payload)
      const userIds: number[] = []
      response.data.discuss_infos.forEach(c => {
        userIds.push(c.creator.id)
        c.reply_discuss.forEach(_c => {
          userIds.push(_c.creator.id)
        })
      })
      yield put({
        type: 'global/fetchUserAvatar',
        payload: Array.from(new Set(userIds))
      })
      yield put({
        type: 'saveDiscussList',
        payload: {
          id: unique_id,
          comment_type,
          data: response.data.discuss_infos || {}
        }
      })
    },
    *createDiscuss({ payload, callback }, { call, put }) {
      const response = yield call(createDiscuss, { discuss: payload })
      if (response.code == 0) {
        yield put({
          type: 'refreshDiscussList',
          payload: {
            ...payload,
            unique_id: `${payload.project_id}-${payload.comment_to_id}`
          }
        })
        callback && callback(response)
      }
    },
    *updateDiscuss({ payload, callback }, { call, put }) {
      const response = yield call(updateDiscuss, { discuss: payload })
      if (response.code == 0) {
        yield put({
          type: 'refreshDiscussList',
          payload: {
            ...payload,
            unique_id: `${payload.project_id}-${payload.comment_to_id}`
          }
        })
        callback && callback(response)
      }
    },
    *deleteDiscuss({ payload, callback }, { call, put }) {
      const response = yield call(deleteDiscuss, payload)
      callback && callback(response)
    },
    *likeDiscuss({ payload, callback }, { call, put }) {
      const { comment_type = 1, unique_id } = payload
      const response = yield call(likeDiscuss, payload)
      yield put({
        type: 'updateDiscussLike',
        payload: {
          id: unique_id,
          comment_type,
          type: 1,
          data: payload
        }
      })
      callback && callback(response)
    },
    *unlikeDiscuss({ payload, callback }, { call, put }) {
      const { comment_type = 1, unique_id } = payload
      const response = yield call(unlikeDiscuss, payload)
      yield put({
        type: 'updateDiscussLike',
        payload: {
          id: unique_id,
          comment_type,
          type: 2,
          data: payload
        }
      })
      callback && callback(response)
    }
  },
  reducers: {
    saveDiscussList(state = initialState, { payload }) {
      const { comment_type, data, id } = payload
      let result = {}
      if (comment_type == 1) {
        result = {
          log_comment: {
            ...state.log_comment,
            [id]: data
          }
        }
      } else if (comment_type == 2) {
        result = {
          memo_comment: {
            ...state.memo_comment,
            [id]: data
          }
        }
      } else if (comment_type == 3) {
        result = {
          company_info_comment: {
            ...state.company_info_comment,
            [id]: data
          }
        }
      } else if (comment_type == 4) {
        result = {
          minutes_meeting_comment: {
            ...state.minutes_meeting_comment,
            [id]: data
          }
        }
      } else if (comment_type == 5) {
        result = {
          company_document_comment: {
            ...state.company_document_comment,
            [id]: data
          }
        }
      } else if (comment_type == 6) {
        result = {
          touhou_manage_comment: {
            ...state.touhou_manage_comment,
            [id]: data
          }
        }
      } else if (comment_type == 7) {
        result = {
          pipeline_comment: {
            ...state.pipeline_comment,
            [id]: data
          }
        }
      } else if (comment_type == 8) {
        result = {
          spa_comment: {
            ...state.spa_comment,
            [id]: data
          }
        }
      } else if (comment_type == 9) {
        result = {
          pipeline_return_investment: {
            ...state.pipeline_return_investment,
            [id]: data
          }
        }
      } else if (comment_type == 10) {
        result = {
          portfolio_return_investment: {
            ...state.portfolio_return_investment,
            [id]: data
          }
        }
      } else if (comment_type == 11) {
        result = {
          sub_memo: {
            ...state.sub_memo,
            [id]: data
          }
        }
      }

      return {
        ...state,
        ...result
      }
    },
    updateDiscussLike(state = initialState, { payload }) {
      const {
        type,
        data: { discuss_id },
        comment_type,
        id
      } = payload

      const temp = state
      let current = []

      if (comment_type == 1) {
        current = temp.log_comment[id] || []
      } else if (comment_type == 2) {
        current = temp.memo_comment[id] || []
      } else if (comment_type == 3) {
        current = temp.company_info_comment[id] || []
      } else if (comment_type == 4) {
        current = temp.minutes_meeting_comment[id] || []
      } else if (comment_type == 5) {
        current = temp.company_document_comment[id] || []
      } else if (comment_type == 6) {
        current = temp.touhou_manage_comment[id] || []
      } else if (comment_type == 7) {
        current = temp.pipeline_comment[id] || []
      } else if (comment_type == 8) {
        current = temp.spa_comment[id] || []
      } else if (comment_type == 9) {
        current = temp.pipeline_return_investment[id] || []
      } else if (comment_type == 10) {
        current = temp.portfolio_return_investment[id] || []
      } else if (comment_type == 11) {
        current = temp.sub_memo[id] || []
      }
      for (let index = 0; index < current.length; index++) {
        const element = current[index]
        if (element.id == discuss_id) {
          element.like_count += type == 1 ? 1 : -1
          element.like_status = type == 1 ? 1 : 0
          break
        } else if (element.reply_discuss && element.reply_discuss.length > 0) {
          for (let j = 0; j < element.reply_discuss.length; j++) {
            const sub = element.reply_discuss[j]
            if (sub.id == discuss_id) {
              sub.like_count += type == 1 ? 1 : -1
              sub.like_status = type == 1 ? 1 : 0
              break
            }
          }
        }
      }

      return {
        ...temp
      }
    }
  }
}
export default CommentModel
